import { collaborateApi, useLazyGetCreatedCommentQuery, useLazyGetDeletedCommentQuery, useLazyGetUpdatedCommentQuery } from 'api/collaborateApi';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { GetTasksResponse } from 'types/api/collaborate';
import { Comment, TaskAttributes } from 'types/api/tasks';
import { useAppDispatch, useAppSelector } from '../store';
import { getSelectedSiteId } from '../store/siteInfoSlice';
import {
  getSelectedTask,
  getTasks,
  setClaroPending,
  setSelectedTask,
  setTasks
} from '../store/tasksSlice';
import { getUser } from '../store/userSlice';

// Update queue types
interface UpdateOperation {
  id: string;
  type: 'create' | 'update' | 'delete';
  commentId: string;
  timestamp: number;
  execute: () => Promise<void>;
}

export function useRealtimeComments() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const selectedTask = useAppSelector(getSelectedTask);
  const tasks = useAppSelector(getTasks);
  const siteId = useAppSelector(getSelectedSiteId);
  const [getComment] = useLazyGetCreatedCommentQuery();
  const [getUpdatedComment] = useLazyGetUpdatedCommentQuery();
  const [getDeletedComment] = useLazyGetDeletedCommentQuery();

  // Queue management
  const updateQueue = useRef<UpdateOperation[]>([]);
  const isProcessing = useRef(false);

  // Check if incoming comment is newer than existing one
  const isCommentNewer = useCallback((incomingComment: Comment, existingComment?: Comment): boolean => {
    if (!existingComment) return true;

    const incomingTime = new Date(incomingComment.updated_at).getTime();
    const existingTime = new Date(existingComment.updated_at).getTime();

    return incomingTime > existingTime;
  }, []);

  const updateTaskInStore = useCallback((task_id: string, updatedTask: Partial<TaskAttributes>, comment: Comment) => {
    // Update the selected task if it's the same task
    if (selectedTask && selectedTask.task_id === updatedTask.task_id) {
      dispatch(setSelectedTask(updatedTask));
      dispatch(collaborateApi.util.updateQueryData('getTask', task_id, (draft) => {
        draft.result = {
          ...draft.result,
          ...updatedTask
        };
      }));
    }

    // Update the task in the tasks list
    const taskIndex = tasks.findIndex(t => t.task_id === task_id);
    if (taskIndex > -1) {
      const updatedTasks = [...tasks];
      const latestComment: GetTasksResponse['result']['tasks'][number]['latest_comment'] = {
        author: {
          avatar: comment.avatar,
          email: comment.email,
          id: comment.user_id,
          user_name: comment.user_name
        },
        comments: comment.comments,
        comment_type: comment.comment_type,
        days: comment.days,
        id: comment.id
      };
      updatedTasks[taskIndex] = {
        ...updatedTasks[taskIndex],
        is_unread: true,
        latest_comment: latestComment
      };
      dispatch(setTasks(updatedTasks));
    }

  }, [dispatch, selectedTask, tasks]);

  // Process queue sequentially
  const processQueue = useCallback(async () => {
    if (isProcessing.current || updateQueue.current.length === 0) {
      return;
    }

    isProcessing.current = true;

    try {
      while (updateQueue.current.length > 0) {
        // Get the oldest operation (FIFO)
        const operation = updateQueue.current.shift();
        if (!operation) break;

        try {
          await operation.execute();
        } catch (error) {
          console.error(`Failed to process ${operation.type} update for comment ${operation.commentId}:`, error);
        }
      }
    } finally {
      isProcessing.current = false;
    }
  }, []);

  // Add update to queue
  const queueUpdate = useCallback((operation: Omit<UpdateOperation, 'id' | 'timestamp'>) => {
    const update: UpdateOperation = {
      ...operation,
      id: `${operation.type}-${operation.commentId}-${Date.now()}`,
      timestamp: Date.now()
    };

    updateQueue.current.push(update);

    // Process queue if not already processing
    if (!isProcessing.current) {
      processQueue();
    }
  }, [processQueue]);

  const handleCommentCreated = useCallback(
    async (e: { id: string, task_id: string }) => {
      queueUpdate({
        type: 'create',
        commentId: e.id,
        execute: async () => {
          try {
            const { task_id } = e;
            const newComment = await getComment(e.id).unwrap();

            if (!newComment) {
              return;
            }

            // Don't add comment if it's from the current user (to avoid duplicates with optimistic updates)
            if (user && newComment.user_normal_id === user.normal_id) {
              return;
            }

            // Check if comment already exists and if incoming is newer
            const existingComment = selectedTask?.comments?.find(c => c.id === newComment.id);
            if (existingComment && !isCommentNewer(newComment, existingComment)) {
              return;
            }

            // Update the task with the new comment
            if (task_id) {
              if (selectedTask && selectedTask.task_id === task_id) {
                const updatedComments = existingComment
                  ? selectedTask.comments?.map(c => c.id === newComment.id ? newComment : c) || []
                  : [...(selectedTask.comments || []), newComment];

                const updatedTask = {
                  ...selectedTask,
                  comments: updatedComments
                };

                updateTaskInStore(task_id, updatedTask, newComment);
              } else {
                const taskIndex = tasks.findIndex(t => t.task_id === task_id);
                if (taskIndex > -1) {
                  const updatedTasks = [...tasks];
                  const latestComment: GetTasksResponse['result']['tasks'][number]['latest_comment'] = {
                    author: {
                      avatar: newComment.avatar,
                      email: newComment.email,
                      id: newComment.user_id,
                      user_name: newComment.user_name
                    },
                    comments: newComment.comments,
                    comment_type: newComment.comment_type,
                    days: newComment.days,
                    id: newComment.id
                  };
                  updatedTasks[taskIndex] = {
                    ...updatedTasks[taskIndex],
                    is_unread: selectedTask?.task_id !== task_id ? true : false,
                    latest_comment: latestComment
                  };
                  dispatch(setTasks(updatedTasks));
                  dispatch(collaborateApi.util.invalidateTags([{ type: 'CollaborateTasks', id: task_id }]));
                }
              }

            }
          } catch (err) {
            console.error('Error fetching comment:', err);
            toast.error('Failed to fetch comment details.');
          }
        }
      });
    },
    [tasks, dispatch, user, getComment, updateTaskInStore, selectedTask, queueUpdate, isCommentNewer]
  );

  const handleCommentUpdated = useCallback(
    async (e: { id: string, task_id: string }) => {
      queueUpdate({
        type: 'update',
        commentId: e.id,
        execute: async () => {
          try {
            const { task_id } = e;

            const updatedComment = await getUpdatedComment(e.id).unwrap();

            if (!updatedComment) {
              return;
            }

            if (updatedComment.comment_clarification && e.task_id) {
              dispatch(setClaroPending({ task_id: e.task_id, pending: false }));
            }

            // Update the comment in the selected task
            if (task_id) {
              if (selectedTask && selectedTask.task_id === task_id && selectedTask.comments) {
                const commentIndex = selectedTask.comments.findIndex(c => c.id === e.id);
                if (commentIndex !== -1) {
                  const existingComment = selectedTask.comments[commentIndex];

                  // Only update if the incoming comment is newer
                  if (!isCommentNewer(updatedComment, existingComment)) {
                    return;
                  }

                  const updatedComments = [...selectedTask.comments];
                  updatedComments[commentIndex] = {
                    ...updatedComments[commentIndex],
                    ...updatedComment
                  };

                  const updatedTask = {
                    ...selectedTask,
                    comments: updatedComments
                  };

                  updateTaskInStore(task_id, updatedTask, updatedComment);
                }
              } else {
                const taskIndex = tasks.findIndex(t => t.task_id === task_id);
                if (taskIndex > -1) {
                  const updatedTasks = [...tasks];
                  updatedTasks[taskIndex] = {
                    ...updatedTasks[taskIndex],
                    is_unread: selectedTask?.task_id !== task_id ? true : false
                  };
                  dispatch(setTasks(updatedTasks));
                }
              }
            }
          } catch (err) {
            console.error('Error fetching updated comment:', err);
            toast.error('Failed to fetch comment details.');
          }
        }
      });
    },
    [dispatch, getUpdatedComment, selectedTask, updateTaskInStore, queueUpdate, isCommentNewer, tasks]
  );

  const handleCommentDeleted = useCallback(
    async (e: { id: string, task_id: string }) => {
      queueUpdate({
        type: 'delete',
        commentId: e.id,
        execute: async () => {
          try {

            const { task_id } = e;

            if (!selectedTask?.comments?.find((c) => c.id === e.id)) {
              return;
            }

            const deletedComment = await getDeletedComment(e.id).unwrap();

            if (!deletedComment) {
              return;
            }

            // Update the comment in the selected task
            if (selectedTask && selectedTask.task_id === task_id && selectedTask.comments) {
              const commentIndex = selectedTask.comments.findIndex(c => c.id === deletedComment.id);
              if (commentIndex !== -1) {
                // For deleted comments, always apply the deletion (no need to check if newer)
                const updatedComments = [...selectedTask.comments];
                updatedComments[commentIndex] = {
                  ...updatedComments[commentIndex],
                  comment_type: deletedComment.comment_type,
                  comments: deletedComment.comments,
                  is_deleted: deletedComment.is_deleted
                };

                const updatedTask = {
                  ...selectedTask,
                  comments: updatedComments
                };

                // Update the selected task if it's the same task
                if (selectedTask && selectedTask.task_id === task_id) {
                  dispatch(setSelectedTask(updatedTask));
                }

                // Update the task in the tasks list
                const taskIndex = tasks.findIndex(t => t.task_id === task_id);
                if (taskIndex > -1) {
                  const updatedTasks = [...tasks];
                  updatedTasks[taskIndex] = {
                    ...updatedTasks[taskIndex],
                    // Don't set is_unread for deleted comments
                  };
                  dispatch(setTasks(updatedTasks));
                }
              }
            }
          } catch (err) {
            console.error('Error fetching deleted comment:', err);
            toast.error('Failed to fetch comment details.');
          }
        }
      });
    },
    [dispatch, getDeletedComment, selectedTask, queueUpdate, tasks]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      updateQueue.current = [];
      isProcessing.current = false;
    };
  }, []);

  // Listen to task comments channel for comment events using raw Echo API
  useEffect(() => {
    if (!siteId || !window.Echo) return;

    const channelName = `sites.${siteId}.comments`;
    const channel = window.Echo.channel(channelName);

    // Listen for all comment events on the same channel
    channel.listen('.CommentCreated', handleCommentCreated);
    channel.listen('.CommentUpdated', handleCommentUpdated);
    channel.listen('.CommentDeleted', handleCommentDeleted);

    // Cleanup function
    return () => {
      channel.stopListening('.CommentCreated');
      channel.stopListening('.CommentUpdated');
      channel.stopListening('.CommentDeleted');
      window.Echo.leaveChannel(channelName);
    };
  }, [siteId, handleCommentCreated, handleCommentUpdated, handleCommentDeleted]);
}